import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, UserPlus, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import CountryPhoneFields from "@/components/CountryPhoneFields";

const inputCls =
  "flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent";

const BLANK = { name: "", username: "", email: "", phone: "", country_code: null, password: "", job_title: "Secretary", notes: "", template: "secretary", active: true };

export default function CreateCollaborator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState(BLANK);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/collaborators/catalog").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { country_code, ...payload } = form;
      const { data } = await api.post("/collaborators", payload);
      // Apply country selection after creation (backend guards mismatch).
      if (country_code && data?.id) {
        try {
          await api.patch(`/admin/users/${data.id}/country`, { country_code });
        } catch (err) {
          if (err.response?.status === 409) {
            const confirmed = window.confirm(
              (err.response.data?.detail || "") +
              "\n\nSave the collaborator with this mismatched country anyway?",
            );
            if (confirmed) {
              await api.patch(`/admin/users/${data.id}/country`, {
                country_code, mismatch_confirmed: true,
              });
            }
          } else { throw err; }
        }
      }
      toast.success(t("collab.created", "Collaborator created"));
      navigate(`/collaborators/${data.id}/permissions`);
    } catch (err) {
      const m = err.response?.data?.detail || t("collab.create_failed", "Create failed");
      setError(typeof m === "string" ? m : "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="collab-create-page">
      <button onClick={() => navigate("/collaborators")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> {t("common.back", "Back")}</button>
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Collaborators</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {t("collab.create_title", "Create collaborator")}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">{t("collab.create_hint", "Start from a template — you can fine-tune permissions after creating.")}</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("ui.full_name")} required>
            <input data-testid="collab-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          </Field>
          <Field label={t("ui.username")} required>
            <input data-testid="collab-username" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })} className={inputCls} />
          </Field>
          <Field label={t("ui.email")} required>
            <input data-testid="collab-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          </Field>
          <Field label={t("collab.temp_password", "Temporary password")} required>
            <input data-testid="collab-password" required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} minLength={6} />
          </Field>
          <Field label={t("collab.job_title", "Job title")}>
            <input data-testid="collab-job" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className={inputCls} placeholder="Secretary, Salesperson, ..." />
          </Field>
          <Field label={t("collab.notes", "Notes")} full>
            <textarea data-testid="collab-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
          </Field>
        </div>

        <CountryPhoneFields
          phone={form.phone}
          countryCode={form.country_code}
          onPhoneChange={(v) => setForm({ ...form, phone: v })}
          onCountryChange={(v) => setForm({ ...form, country_code: v })}
          testIdPrefix="collab"
        />

        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#0F62FE] mb-2">{t("collab.template", "Permission template")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {templates.map((t2) => (
              <label key={t2.key} data-testid={`collab-template-${t2.key}`} className={"cursor-pointer rounded-lg border p-3 flex items-start gap-3 hover:bg-slate-50 " + (form.template === t2.key ? "border-[#0F62FE] bg-[#EDF5FF]" : "border-slate-200")}>
                <input type="radio" name="template" checked={form.template === t2.key} onChange={() => setForm({ ...form, template: t2.key })} className="mt-0.5" />
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 text-sm">{t2.label}</div>
                  <div className="text-xs text-slate-500 leading-snug mt-0.5">{t2.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> {t("ui.active_label")}</label>

        {error && (<div className="rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2">{error}</div>)}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => navigate("/collaborators")} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">{t("common.cancel")}</button>
          <button type="submit" data-testid="collab-submit" disabled={busy} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {t("collab.create_submit", "Create collaborator")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, required, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">
        {label}{required && <span className="text-rose-500"> *</span>}
      </label>
      {children}
    </div>
  );
}
